import { useState } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
} from '@tanstack/react-router';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { ProtectedRoute } from '@/components/protected-route';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';

import JobTaskListView from '@/components/tasks/list';

import { selectJobOverviewData, selectTasksByJobId, selectCheckinByBooking } from '@/store/selectors';
import { addJobState, updateJobState } from '@/store/actions';
import { updateHelpModal, updateNotesModal } from '@/store/slices/appStateSlice';
import type { RootState } from '@/store';

export const Route = createFileRoute('/job/$jobId/$date/walkthrough')({
  component: () => (
    <ProtectedRoute>
      <JobWalkthroughPage />
    </ProtectedRoute>
  ),
});

function JobWalkthroughPage() {
  const { jobId, date } = useParams({ from: '/job/$jobId/$date/walkthrough' });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get job data from Redux
  const { booking, job, jobState } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  );

  const checkinToday = useSelector((state: RootState) =>
    booking ? selectCheckinByBooking(state, booking.id, booking.date) : null
  );

  // Get tasks for this job
  const tasks = useSelector((state: RootState) => selectTasksByJobId(state, jobId));

  const handleGoBack = () => {
    navigate({ to: '/job/$jobId/$date', params: { jobId, date } });
  };

  const handleUpdateJobState = () => {
    if (typeof jobState !== 'undefined') {
      dispatch(updateJobState({
        ...jobState,
        confirmed_walkthrough: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    } else {
      dispatch(addJobState({
        job_id: job.id,
        confirmed_walkthrough: moment().format('YYYY-MM-DD HH:mm:ss')
      }));
    }

    // Navigate to health and safety next
    navigate({ to: '/job/$jobId/$date/health-safety', params: { jobId, date } });
  };

  const showNotesModal = () => {
    dispatch(updateNotesModal({
      show: true,
      canAssignTask: true,
    }));
  };

  const showHelpModal = () => {
    dispatch(updateHelpModal(true));
  };

  if (!job || !booking) {
    return (
      <div className="col-span-2 flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Job not found</h2>
          <p className="text-gray-500">Could not load job data</p>
        </div>
      </div>
    );
  }

  const isWalkthroughComplete = jobState?.confirmed_walkthrough !== null;
console.log("isWalkthroughComplete jobState", jobState, isWalkthroughComplete);

  return (
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex justify-center">
        <div className="max-w-4xl w-full space-y-8">

          <div className="space-y-4">
            <h1 className="text-3xl font-bold leading-tight m-0">
              Please walk through each task with the customer.
            </h1>

            <p className="text-base leading-relaxed mt-4">
              For each task please carry out the following…
            </p>

            <div className="space-y-1">
              <p className="text-base leading-relaxed text-black m-0">
                1. Discuss the requirements with the customer.
              </p>
              <p className="text-base leading-relaxed text-black m-0">
                2. Answer any questions they may have.
              </p>
              <p className="text-base leading-relaxed text-black m-0">
                3. Take photos of the task to be carried out.
              </p>
              <p className="text-base leading-relaxed text-black mb-4">
                4. Add a note (above) if the customer has any comments.
              </p>
            </div>
          </div>

          {/* Tasks */}
          <div className="space-y-4">
            <Label className="text-gray-800 font-semibold text-sm uppercase">
              Tasks
            </Label>
            <JobTaskListView
              canAddPhotos={true}
              canUpdateTimesheet={true}
              updateJobStatus={true}
              checkin={checkinToday}
              tasks={tasks}
            />
          </div>

          <div className="flex gap-4 py-8 border-t border-gray-200">
            <Button
              onClick={isWalkthroughComplete ? handleGoBack : handleUpdateJobState}
              className="flex-1"
            >
              {isWalkthroughComplete ? "Back to Overview" : "Proceed with Job"}
            </Button>

            <Button
              variant="secondary"
              onClick={showHelpModal}
              className="flex-1"
            >
              I Need Assistance
            </Button>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
